import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ReportSummary {
  total_aud: number;
  change_24h_aud: number | null;
  change_24h_pct: number | null;
  holdings_count: number;
  top_winners: { symbol: string; pct: number | null }[];
  top_losers: { symbol: string; pct: number | null }[];
  largest_position: { symbol: string; share_pct: number | null } | null;
  concentration_risk: string | null;
  top_actions: string[];
  market_vibe: string | null;
}

export interface PortfolioReport {
  id: string;
  report_date: string;
  status: string;
  summary: ReportSummary | null;
  markdown: string | null;
  error: string | null;
  created_at: string;
}

export function useSwyftxCredential() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["swyftx-cred", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from("user_swyftx_credentials")
        .select("key_last4, report_enabled, last_verified_at, last_manual_run_at")
        .maybeSingle();
      return data;
    },
  });
}

export function usePortfolioReports(limit = 14) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["portfolio-reports", user?.id, limit],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("portfolio_reports")
        .select("id, report_date, status, summary, markdown, error, created_at")
        .order("report_date", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as PortfolioReport[];
    },
    // Poll while a report is being generated so an open tab updates itself.
    refetchInterval: (q) => {
      const rows = q.state.data as PortfolioReport[] | undefined;
      return rows?.[0]?.status === "running" ? 10_000 : 5 * 60_000;
    },
  });
}

export const fmtAud = (n: number | null | undefined) =>
  n == null ? "n/a" : `A$${n.toLocaleString("en-AU", { maximumFractionDigits: n < 10 ? 2 : 0 })}`;
export const fmtPct = (n: number | null | undefined) =>
  n == null ? "n/a" : `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;
